<template>
  <b-modal
    ref="modal"
    :id="'warming-modal-' + _uid"
    centered
    hide-header
    hide-footer
    no-close-on-backdrop
    no-close-on-esc
    dialog-class="custom-warning-modal-dialog"
    content-class="custom-warning-modal-content"
    body-class="p-0"
  >
    <div class="d-flex flex-column align-items-center" style="gap: 20px; padding: 24px 20px;">
      <!-- Dynamic Icon -->
      <svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="40" cy="40" r="40" :fill="bgColor"/>
        <path d="M13 40C13 54.9117 25.0883 67 40 67C54.9117 67 67 54.9117 67 40C67 25.0883 54.9117 13 40 13C25.0883 13 13 25.0883 13 40Z" :fill="'url(#gradient-warming-modal-' + _uid + '-' + variantTheme + ')'"/>
        <!-- Cross for danger, Exclamation for warning/info -->
        <path v-if="variantTheme === 'danger'" fill-rule="evenodd" clip-rule="evenodd" d="M49.2678 30.7323C50.2441 31.7086 50.2441 33.2915 49.2677 34.2678L43.5352 40L49.2677 45.7322C50.2441 46.7085 50.2441 48.2914 49.2678 49.2677C48.2915 50.2441 46.7086 50.2441 45.7323 49.2678L39.9995 43.5354L34.2677 49.2668C33.2914 50.2431 31.7085 50.2431 30.7322 49.2667C29.7559 48.2904 29.7559 46.7075 30.7323 45.7312L36.4639 40L30.7323 34.2688C29.7559 33.2925 29.7559 31.7096 30.7322 30.7333C31.7085 29.7569 33.2914 29.7569 34.2677 30.7332L39.9995 36.4646L45.7323 30.7322C46.7086 29.7559 48.2915 29.7559 49.2678 30.7323Z" fill="white"/>
        <path v-else fill-rule="evenodd" clip-rule="evenodd" d="M40 26C41.6569 26 43 27.3431 43 29V45C43 46.6569 41.6569 48 40 48C38.3431 48 37 46.6569 37 45V29C37 27.3431 38.3431 26 40 26ZM40 50C41.6569 50 43 51.3431 43 53C43 54.6569 41.6569 56 40 56C38.3431 56 37 54.6569 37 53C37 51.3431 38.3431 50 40 50Z" fill="white"/>
        <defs>
          <linearGradient :id="'gradient-warming-modal-' + _uid + '-danger'" x1="67" y1="13" x2="5.58259" y2="62.9405" gradientUnits="userSpaceOnUse">
            <stop offset="0.046875" stop-color="#EA1637"/>
            <stop offset="1" stop-color="#FF6C6C"/>
          </linearGradient>
          <linearGradient :id="'gradient-warming-modal-' + _uid + '-warning'" x1="67" y1="13" x2="5.58259" y2="62.9405" gradientUnits="userSpaceOnUse">
            <stop offset="0.046875" stop-color="#D97706"/>
            <stop offset="1" stop-color="#FBBF24"/>
          </linearGradient>
          <linearGradient :id="'gradient-warming-modal-' + _uid + '-info'" x1="67" y1="13" x2="5.58259" y2="62.9405" gradientUnits="userSpaceOnUse">
            <stop offset="0.046875" stop-color="#0284C7"/>
            <stop offset="1" stop-color="#38BDF8"/>
          </linearGradient>
        </defs>
      </svg>
      <!-- Title & Description Container -->
      <div class="d-flex flex-column align-items-center" style="gap: 8px; width: 100%;">
        <!-- Title -->
        <h4 class="font-weight-bold m-0 text-center" style="color: #0f172a; font-size: 20px; line-height: 1.4;">
          {{ computedTitle }}
        </h4>
        <!-- Description -->
        <p class="text-secondary" style="font-size: 15px; line-height: 1.6; text-align: justify; text-align-last: center; padding: 0 16px; margin: 0 !important;" v-html="computedDescription">
        </p>
      </div>
      <!-- Error Code Display -->
      <div v-if="computedErrorCode" class="text-center" style="color: #94a3b8; font-size: 13px; font-weight: 200; margin-top: -10px; margin-bottom: -6px; line-height: 1;">
        {{ $t('errorCodeLabel') }}: {{ computedErrorCode }}
      </div>
      <!-- Buttons Container -->
      <div class="d-flex flex-row justify-content-center align-items-center" style="gap: 12px; width: 100%; padding-top: 12px;">
        <!-- Primary Button (Close) -->
        <b-button
          variant="primary"
          class="px-5 py-2 m-0"
          style="background-color: #034ea2; border-color: #034ea2; font-weight: 600; border-radius: 6px;"
          @click="hide()"
        >
          {{ errorConfig.primaryBtnText }}
        </b-button>
      </div>
    </div>
  </b-modal>
</template>

<script lang="ts">
import { Component, Prop } from 'vue-property-decorator';
import BaseComponent from '@/components/BaseComponent';
import { getMeetingErrorConfig, meetingErrorMapping } from '@/service/meetingErrorMapping';

@Component
export default class WarmingModal extends BaseComponent {
  @Prop({ default: "", type: String }) description!: string;
  @Prop({ default: "", type: String }) title!: string;
  @Prop({ default: "", type: String }) errorCode!: string;

  currentError: any = null;

  get errorObj() {
    return this.currentError;
  }

  get computedErrorCode() {
    if (this.errorObj) {
      return this.errorObj.response?.data?.code || this.errorObj.code || '';
    }
    return this.errorCode;
  }

  get computedDescription() {
    if (this.errorObj) {
      return this.returnErrorMessage(this.errorObj);
    }
    return this.description;
  }

  get computedTitle() {
    return this.title || this.errorConfig.title;
  }

  get errorConfig() {
    return getMeetingErrorConfig(this.computedErrorCode, this);
  }

  get variantTheme() {
    return this.errorConfig.variant || 'danger';
  }

  get bgColor() {
    switch (this.variantTheme) {
      case 'warning': return '#FEF3C7';
      case 'info': return '#E0F2FE';
      case 'danger':
      default:
        return '#FEEBEC';
    }
  }

  show(error?: any) {
    if (error) {
      this.currentError = error;
    }

    const err = this.errorObj;
    if (err) {
      const code = err.response?.data?.code || err.code || "";
      if (meetingErrorMapping[code]) {
        (this.$refs.modal as any).show();
      } else {
        this.toastError(err);
      }
    } else {
      this.toastErrorMessage(this.$t("khongxacdinh").toString());
    }
  }

  hide() {
    (this.$refs.modal as any).hide();
  }
}
</script>

<style lang="scss" scoped>
  ::v-deep .custom-warning-modal-dialog {
    max-width: 440px !important;
    width: 440px !important;
    margin: 1.75rem auto;
  }

  ::v-deep .custom-warning-modal-content {
    border-radius: 16px !important;
    border: none !important;
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.08) !important;
  }
</style>
